export interface AppConfig {
  supabaseUrl: string;
  supabaseAnonKey: string;
  apiBaseUrl: string;
}

interface RuntimeConfig {
  supabaseUrl?: string;
  supabaseAnonKey?: string;
  apiBaseUrl?: string;
}

declare global {
  interface Window {
    __FACTORIUM_CONFIG__?: RuntimeConfig;
  }
}

export type ConfigResult =
  | { ok: true; config: AppConfig }
  | { ok: false; missing: string[] };

/**
 * Resolves configuration from the runtime `config.js` first and Vite's build-time
 * environment second.
 *
 * The runtime source exists so the deployed image is not welded to one Supabase
 * instance: changing an endpoint is a container restart, not a rebuild. Local
 * development has no such file worth generating, so `.env.local` still works.
 *
 * Missing values are returned rather than thrown, so the app can render an
 * explanation instead of a blank page — a misconfigured deploy should say so.
 */
export function loadConfig(): ConfigResult {
  const runtime = window.__FACTORIUM_CONFIG__ ?? {};
  const missing: string[] = [];

  const read = (
    runtimeValue: string | undefined,
    buildValue: string | undefined,
    name: string,
  ): string => {
    // The entrypoint writes empty strings for variables that were never set, so
    // treat blank as absent rather than as a deliberate value.
    const value = (runtimeValue || buildValue || "").trim();
    if (!value) missing.push(name);
    return value;
  };

  const config: AppConfig = {
    supabaseUrl: read(runtime.supabaseUrl, import.meta.env.VITE_SUPABASE_URL, "SUPABASE_URL"),
    supabaseAnonKey: read(
      runtime.supabaseAnonKey,
      import.meta.env.VITE_SUPABASE_ANON_KEY,
      "SUPABASE_ANON_KEY",
    ),
    apiBaseUrl: read(runtime.apiBaseUrl, import.meta.env.VITE_API_BASE_URL, "API_BASE_URL"),
  };

  return missing.length > 0 ? { ok: false, missing } : { ok: true, config };
}
